/**
 * GoogleBusinessHoursPage — Admin: Google-Öffnungszeiten automatisch aus dem
 * Terminkalender pflegen (Google Business Profile API).
 *
 * Fluss: OAuth-Zugangsdaten speichern → mit Google verbinden → Studio↔Standort
 * zuordnen → Vorschau der nächsten Tage → automatisch täglich syncen.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Save, Link2, Unlink, RefreshCw, CheckCircle2, AlertTriangle,
  Clock, MapPin, Info, ExternalLink, Copy, CalendarClock, ChevronDown, ShieldCheck
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const GoogleBusinessHoursPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState([]);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [mappings, setMappings] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [rounding, setRounding] = useState(15);
  const [hintEnabled, setHintEnabled] = useState(false);
  const [hintText, setHintText] = useState('Training nur nach Terminvereinbarung.');

  const loadStatus = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/google-business/settings`, authHeader());
    setStatus(data);
    setClientId(data.client_id || '');
    setRedirectUri(data.redirect_uri || '');
    setSelectedAccount(data.account_name || '');
    setMappings(data.studio_locations || {});
    setRounding(data.rounding_minutes ?? 15);
    setHintEnabled(!!data.appointment_hint_enabled);
    setHintText(data.appointment_hint_text || 'Training nur nach Terminvereinbarung.');
    return data;
  }, []);

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/google-business/preview`, authHeader());
      setPreview(data.studios || []);
    } catch (e) {
      console.error('preview error', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadStatus();
        await loadPreview();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
      // OAuth-Rückkehr auswerten
      const params = new URLSearchParams(window.location.search);
      if (params.get('google') === 'connected') {
        toast.success('Google-Konto erfolgreich verbunden');
        window.history.replaceState({}, '', window.location.pathname);
      } else if (params.get('google') === 'error') {
        toast.error('Google-Verbindung fehlgeschlagen: ' + (params.get('reason') || 'unbekannt'));
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
  }, [loadStatus, loadPreview]);

  const saveSettings = async (patch, successMsg) => {
    const { data } = await axios.put(`${API}/admin/google-business/settings`, patch, authHeader());
    setStatus(data);
    if (successMsg) toast.success(successMsg);
    return data;
  };

  const handleSaveCreds = async () => {
    setSavingCreds(true);
    try {
      const patch = { client_id: clientId, redirect_uri: redirectUri };
      if (clientSecret) patch.client_secret = clientSecret;
      await saveSettings(patch, 'Zugangsdaten gespeichert');
      setClientSecret('');
    } catch (e) {
      toast.error('Fehler beim Speichern der Zugangsdaten');
    } finally {
      setSavingCreds(false);
    }
  };

  const handleConnect = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/google-business/oauth/start`, authHeader());
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verbindung konnte nicht gestartet werden');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Google-Verbindung wirklich trennen? Der automatische Sync wird deaktiviert.')) return;
    try {
      await axios.post(`${API}/admin/google-business/disconnect`, {}, authHeader());
      toast.success('Verbindung getrennt');
      await loadStatus();
    } catch (e) {
      toast.error('Trennen fehlgeschlagen');
    }
  };

  const handleLoadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const { data } = await axios.get(`${API}/admin/google-business/accounts`, authHeader());
      setAccounts(data.accounts || []);
      if ((data.accounts || []).length === 1) {
        await handleSelectAccount(data.accounts[0].name);
      }
      toast.success(`${(data.accounts || []).length} Google-Konto(en) geladen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konten konnten nicht geladen werden');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleSelectAccount = async (accountName) => {
    setSelectedAccount(accountName);
    try {
      await saveSettings({ account_name: accountName });
      const { data } = await axios.get(
        `${API}/admin/google-business/locations?account_name=${encodeURIComponent(accountName)}`,
        authHeader()
      );
      setLocations(data.locations || []);
      toast.success(`${(data.locations || []).length} Standort(e) gefunden`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Standorte konnten nicht geladen werden');
    }
  };

  const setStudioMapping = (studioId, field, value, title) => {
    setMappings((prev) => ({
      ...prev,
      [studioId]: { ...(prev[studioId] || {}), [field]: value, ...(title !== undefined ? { title } : {}) },
    }));
  };

  const handleSaveMappings = async () => {
    try {
      await saveSettings({ studio_locations: mappings }, 'Standort-Zuordnung gespeichert');
      await loadPreview();
    } catch (e) {
      toast.error('Speichern der Zuordnung fehlgeschlagen');
    }
  };

  const handleToggleAuto = async (val) => {
    try {
      await saveSettings({ enabled: val }, val ? 'Automatischer Sync aktiviert' : 'Automatischer Sync deaktiviert');
    } catch (e) {
      toast.error('Umschalten fehlgeschlagen');
    }
  };

  const handleRoundingChange = async (val) => {
    const n = parseInt(val, 10);
    setRounding(n);
    try {
      await saveSettings({ rounding_minutes: n });
      await loadPreview();
      toast.success('Rundung gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleSaveHint = async () => {
    try {
      await saveSettings(
        { appointment_hint_enabled: hintEnabled, appointment_hint_text: hintText },
        'Hinweis-Einstellung gespeichert'
      );
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleSync = async (validateOnly) => {
    setSyncing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/google-business/sync?validate_only=${validateOnly}`, {}, authHeader()
      );
      if (data.ok) {
        toast.success(validateOnly ? 'Test erfolgreich – Daten sind gültig' : 'Öffnungszeiten an Google gesendet');
      } else {
        const err = (data.results || []).find((r) => !r.ok);
        toast.error('Sync-Problem: ' + (err?.error || 'unbekannt'));
      }
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(redirectUri);
    toast.success('Redirect-URI kopiert');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const connected = status?.connected;
  const configured = status?.configured;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="google-business-page">
      <div className="mb-6">
        {!embedded && (
          <Button variant="ghost" onClick={() => navigate('/settings')} className="mb-4" data-testid="gbp-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Einstellungen
          </Button>
        )}
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-100">
            <CalendarClock className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Google Öffnungszeiten</h1>
            <p className="text-sm text-gray-500">
              Aktualisiert die Öffnungszeiten bei Google automatisch anhand deines Terminkalenders.
            </p>
          </div>
        </div>
      </div>

      {/* Status-Banner */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="gbp-status-badges">
        <Badge variant={configured ? 'default' : 'secondary'} className={configured ? 'bg-emerald-600' : ''}>
          {configured ? 'Zugangsdaten hinterlegt' : 'Zugangsdaten fehlen'}
        </Badge>
        <Badge variant={connected ? 'default' : 'secondary'} className={connected ? 'bg-emerald-600' : ''}>
          {connected ? `Verbunden${status?.google_email ? ' · ' + status.google_email : ''}` : 'Nicht verbunden'}
        </Badge>
        <Badge variant={status?.enabled ? 'default' : 'secondary'} className={status?.enabled ? 'bg-blue-600' : ''}>
          {status?.enabled ? 'Auto-Sync AN' : 'Auto-Sync AUS'}
        </Badge>
      </div>

      {/* Freischaltungshinweis */}
      {!connected && (
        <Alert className="mb-6 border-amber-300 bg-amber-50" data-testid="gbp-approval-alert">
          <ShieldCheck className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Wichtig: Google-API-Freischaltung nötig</AlertTitle>
          <AlertDescription className="text-amber-700 text-sm">
            Die Google Business Profile API muss von Google einmalig freigeschaltet werden (Antrag, Dauer:
            Tage bis Wochen). Erst danach funktioniert das Verbinden unten. Die Schritt-für-Schritt-Anleitung
            findest du weiter unten unter „Einrichtung &amp; Freischaltung".
          </AlertDescription>
        </Alert>
      )}

      {/* 1. Zugangsdaten */}
      <Card className="mb-6" data-testid="gbp-credentials-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Link2 className="h-5 w-5 text-gray-500" /> 1. Google-Zugangsdaten (OAuth)
          </CardTitle>
          <CardDescription>
            Aus deinem Google-Cloud-Projekt (OAuth-Client vom Typ „Webanwendung").
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="gbp-client-id">Client-ID</Label>
            <Input
              id="gbp-client-id" data-testid="gbp-client-id-input"
              value={clientId} onChange={(e) => setClientId(e.target.value)}
              placeholder="123456789-abc.apps.googleusercontent.com"
            />
          </div>
          <div>
            <Label htmlFor="gbp-client-secret">Client-Secret {status?.client_secret_set && <span className="text-xs text-emerald-600">(hinterlegt – leer lassen zum Beibehalten)</span>}</Label>
            <Input
              id="gbp-client-secret" data-testid="gbp-client-secret-input" type="password"
              value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
              placeholder={status?.client_secret_set ? '••••••••••••' : 'GOCSPX-...'}
            />
          </div>
          <div>
            <Label htmlFor="gbp-redirect">Autorisierte Weiterleitungs-URI (in Google Cloud eintragen)</Label>
            <div className="flex gap-2">
              <Input id="gbp-redirect" data-testid="gbp-redirect-input" value={redirectUri}
                onChange={(e) => setRedirectUri(e.target.value)} />
              <Button variant="outline" size="icon" onClick={copyRedirect} data-testid="gbp-copy-redirect-btn" title="Kopieren">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Diese exakte URI muss in Google Cloud → Anmeldedaten → OAuth-Client → „Autorisierte Weiterleitungs-URIs" stehen.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={handleSaveCreds} disabled={savingCreds} data-testid="gbp-save-creds-btn">
              {savingCreds ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Zugangsdaten speichern
            </Button>
            {configured && !connected && (
              <Button onClick={handleConnect} variant="default" className="bg-blue-600 hover:bg-blue-700" data-testid="gbp-connect-btn">
                <Link2 className="h-4 w-4 mr-2" /> Mit Google verbinden
              </Button>
            )}
            {connected && (
              <Button onClick={handleDisconnect} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="gbp-disconnect-btn">
                <Unlink className="h-4 w-4 mr-2" /> Verbindung trennen
              </Button>
            )}
          </div>
          {connected && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 pt-1">
              <CheckCircle2 className="h-4 w-4" /> Verbunden{status?.google_email ? ` als ${status.google_email}` : ''}
              {status?.connected_at ? ` seit ${new Date(status.connected_at).toLocaleString('de-CH')}` : ''}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Standort-Zuordnung */}
      <Card className={`mb-6 ${!connected ? 'opacity-60 pointer-events-none' : ''}`} data-testid="gbp-mapping-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-gray-500" /> 2. Studios mit Google-Standorten verbinden
          </CardTitle>
          <CardDescription>Welcher Google-Eintrag gehört zu welchem StrongFit-Studio?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-end gap-3">
            <Button onClick={handleLoadAccounts} disabled={loadingAccounts} variant="outline" data-testid="gbp-load-accounts-btn">
              {loadingAccounts ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Google-Konten laden
            </Button>
            {accounts.length > 0 && (
              <div className="min-w-[280px]">
                <Label className="text-xs">Google-Konto</Label>
                <Select value={selectedAccount} onValueChange={handleSelectAccount}>
                  <SelectTrigger data-testid="gbp-account-select"><SelectValue placeholder="Konto wählen" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.name} value={a.name}>{a.accountName || a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {preview.map((s) => {
            const m = mappings[s.studio_id] || {};
            return (
              <div key={s.studio_id} className="border rounded-lg p-4 space-y-3" data-testid={`gbp-studio-row-${s.studio_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-900">{s.studio_name}</div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Sync aktiv</Label>
                    <Switch
                      checked={!!m.enabled}
                      onCheckedChange={(v) => setStudioMapping(s.studio_id, 'enabled', v)}
                      data-testid={`gbp-studio-enable-${s.studio_id}`}
                    />
                  </div>
                </div>
                <Select
                  value={m.location_name || ''}
                  onValueChange={(v) => {
                    const loc = locations.find((l) => l.name === v);
                    setStudioMapping(s.studio_id, 'location_name', v, loc?.title || '');
                  }}
                >
                  <SelectTrigger data-testid={`gbp-studio-location-${s.studio_id}`}>
                    <SelectValue placeholder={locations.length ? 'Google-Standort wählen' : 'Zuerst Konto laden'} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.name} value={l.name}>
                        {l.title || l.name}
                        {l.storefrontAddress?.locality ? ` · ${l.storefrontAddress.locality}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}

          <Button onClick={handleSaveMappings} data-testid="gbp-save-mapping-btn">
            <Save className="h-4 w-4 mr-2" /> Zuordnung speichern
          </Button>
        </CardContent>
      </Card>

      {/* Darstellung bei Google: Rundung + Termin-Hinweis */}
      <Card className="mb-6" data-testid="gbp-display-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-gray-500" /> Darstellung bei Google
          </CardTitle>
          <CardDescription>Feinschliff, wie die Zeiten &amp; Zusatzinfos bei Google erscheinen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm">Zeiten runden</Label>
            <div className="flex items-center gap-3 mt-1">
              <Select value={String(rounding)} onValueChange={handleRoundingChange}>
                <SelectTrigger className="w-64" data-testid="gbp-rounding-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Keine (exakte Zeiten)</SelectItem>
                  <SelectItem value="15">Viertelstunde (z. B. 06:00–21:00)</SelectItem>
                  <SelectItem value="30">Halbe Stunde</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Öffnung wird abgerundet, Schluss aufgerundet — so bleiben alle Trainings abgedeckt und die Zeiten sehen sauber aus.
            </p>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Hinweis „nur nach Vereinbarung" bei Google anzeigen</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Wird beim Sync in die Google-Unternehmensbeschreibung eingefügt (bestehender Text bleibt erhalten).
                </p>
              </div>
              <Switch checked={hintEnabled} onCheckedChange={setHintEnabled} data-testid="gbp-hint-switch" />
            </div>
            {hintEnabled && (
              <div className="mt-3">
                <Input
                  value={hintText}
                  onChange={(e) => setHintText(e.target.value)}
                  maxLength={200}
                  data-testid="gbp-hint-text-input"
                  placeholder="Training nur nach Terminvereinbarung."
                />
              </div>
            )}
            <Button onClick={handleSaveHint} className="mt-3" variant="outline" data-testid="gbp-save-hint-btn">
              <Save className="h-4 w-4 mr-2" /> Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 3. Vorschau + Sync */}
      <Card className="mb-6" data-testid="gbp-preview-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-gray-500" /> 3. Vorschau &amp; automatischer Sync
              </CardTitle>
              <CardDescription>Diese Öffnungszeiten (nächste {status?.horizon_days || 7} Tage) werden aus dem Kalender berechnet.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-600">Täglich automatisch syncen</Label>
              <Switch checked={!!status?.enabled} onCheckedChange={handleToggleAuto} data-testid="gbp-auto-sync-switch" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={loadPreview} data-testid="gbp-refresh-preview-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Vorschau aktualisieren
            </Button>
            <Button variant="outline" onClick={() => handleSync(true)} disabled={syncing || !connected} data-testid="gbp-sync-test-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Testen (nur prüfen)
            </Button>
            <Button onClick={() => handleSync(false)} disabled={syncing || !connected} className="bg-blue-600 hover:bg-blue-700" data-testid="gbp-sync-now-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Jetzt an Google senden
            </Button>
          </div>

          {status?.last_sync_at && (
            <div className="text-sm text-gray-600 flex items-center gap-2" data-testid="gbp-last-sync">
              {status.last_sync_error
                ? <AlertTriangle className="h-4 w-4 text-red-500" />
                : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
              Letzter Sync: {new Date(status.last_sync_at).toLocaleString('de-CH')}
              {status.last_sync_error ? ` · Fehler: ${status.last_sync_error}` : ' · erfolgreich'}
            </div>
          )}

          <div className="space-y-4">
            {preview.map((s) => (
              <div key={s.studio_id} className="border rounded-lg overflow-hidden" data-testid={`gbp-preview-studio-${s.studio_id}`}>
                <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                  <span className="font-medium text-gray-900">{s.studio_name}</span>
                  {s.mapped
                    ? <Badge className={s.sync_enabled ? 'bg-emerald-600' : 'bg-gray-400'}>{s.sync_enabled ? 'Sync aktiv' : 'zugeordnet, Sync aus'}</Badge>
                    : <Badge variant="secondary">nicht zugeordnet</Badge>}
                </div>
                <div className="divide-y">
                  {s.week.map((d) => (
                    <div key={d.date} className="px-4 py-2 flex items-center justify-between text-sm">
                      <span className="text-gray-700 w-40">
                        {d.weekday}, {new Date(d.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}
                      </span>
                      {d.is_closed
                        ? <span className="text-gray-400">Geschlossen</span>
                        : <span className="font-medium text-gray-900">{d.open} – {d.close} <span className="text-gray-400 font-normal">({d.count} Termine)</span></span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Einrichtungs-Anleitung */}
      <Collapsible open={guideOpen} onOpenChange={setGuideOpen}>
        <Card data-testid="gbp-guide-card">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer">
              <CardTitle className="flex items-center justify-between text-lg">
                <span className="flex items-center gap-2"><Info className="h-5 w-5 text-gray-500" /> Einrichtung &amp; Freischaltung (Anleitung)</span>
                <ChevronDown className={`h-5 w-5 transition-transform ${guideOpen ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="text-sm text-gray-700 space-y-3">
              <ol className="list-decimal ml-5 space-y-2">
                <li>Google-Cloud-Projekt erstellen: <a className="text-blue-600 underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">console.cloud.google.com</a></li>
                <li>APIs aktivieren: „My Business Account Management API" und „My Business Business Information API".</li>
                <li>OAuth-Zustimmungsbildschirm konfigurieren, Scope <code>.../auth/business.manage</code> hinzufügen.</li>
                <li>Anmeldedaten → OAuth-Client-ID (Typ „Webanwendung") erstellen. Die obige Redirect-URI eintragen.</li>
                <li>Client-ID &amp; Client-Secret oben einfügen und speichern.</li>
                <li><strong>API-Freischaltung beantragen</strong> (Pflicht, sonst Quota 0): Formular
                  <a className="text-blue-600 underline ml-1" href="https://support.google.com/business/contact/api_default" target="_blank" rel="noreferrer">support.google.com/business/contact/api_default</a>
                  → „Application for Basic API Access". Voraussetzung: seit ≥60 Tagen verifiziertes Business-Profil, Live-Website mit Datenschutzerklärung, E-Mail-Domain passend zur Website. Bearbeitung: Tage bis Wochen.</li>
                <li>Nach Freischaltung: oben „Mit Google verbinden", dann Studios zuordnen und Auto-Sync aktivieren.</li>
              </ol>
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-xs">
                  Hinweis: Google-Änderungen werden nicht sofort öffentlich sichtbar (kurze Prüf-/Verzögerungszeit).
                  Wir setzen pro Tag die reale Öffnungsspanne (erster bis letzter Termin); Tage ohne Termine = geschlossen.
                </AlertDescription>
              </Alert>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default GoogleBusinessHoursPage;
